/**
 * The floating top-center card shared by the training arrival popover
 * (#4147) and the "What's New" announcement banner (#14953): fixed over the
 * page rather than in flow, so showing one never pushes the app down.
 */

import type { ComponentProps, ReactNode } from 'react'

import { SEMANTIC_COLORS } from '@/lib/color-constants'
import { cn } from '@/lib/utils'

/** The icon tile's neutral (info) tint. */
export const FLOATING_NOTICE_TILE_TINT = SEMANTIC_COLORS.info.bgLight

/** Icon colour for the neutral tile. */
export const FLOATING_NOTICE_ICON_TINT = SEMANTIC_COLORS.info.text

/**
 * Stacking layers. The training popover sits above dialogs (#4147); the
 * announcement banner sits under them, so an open dialog — including the
 * announcement's own popup — is never covered by it (#14953).
 *
 * On sai-ui's layer scale since #16990 (they were `z-[600]` / `z-[450]`):
 * above dialogs is the menu layer, under every feedback layer; below dialogs
 * is the modal-overlay layer, under every dialog and drawer and still over
 * the `z-nav*` app chrome.
 */
const LAYER_CLASS = {
  aboveDialogs: 'z-menu',
  belowDialogs: 'z-modal-overlay',
} as const

export function FloatingNotice({
  icon,
  tileClassName = FLOATING_NOTICE_TILE_TINT,
  controls,
  children,
  inline = false,
  layer = 'aboveDialogs',
  className,
  ...rest
}: {
  /** The tile's icon, sized `h-4 w-4` by the caller. */
  icon: ReactNode
  tileClassName?: string
  /** Buttons to the right of the content (minimize, dismiss). */
  controls?: ReactNode
  children: ReactNode
  /** Render in place instead of fixed — for previews inside a frame. */
  inline?: boolean
  /** Whether the floating card covers open dialogs. Ignored when inline. */
  layer?: keyof typeof LAYER_CLASS
} & ComponentProps<'div'>) {
  return (
    <div
      {...rest}
      className={cn(
        inline
          ? 'mx-auto w-full max-w-md'
          : cn(
              'fixed top-4 left-1/2 mx-auto w-full max-w-md -translate-x-1/2 px-4',
              LAYER_CLASS[layer],
            ),
        className,
      )}
    >
      <div
        className={cn(
          'rounded-xl border border-stroke-regular bg-surface-elevated p-4 shadow-lg animate-in fade-in slide-in-from-top-2 duration-300',
          // Fixed content never scrolls with the page, so a long expanded
          // body scrolls inside the card instead of running off-screen.
          !inline && 'max-h-[calc(100dvh-2rem)] overflow-y-auto',
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 flex-shrink-0 rounded-lg p-2 transition-colors duration-500',
              tileClassName,
            )}
          >
            {icon}
          </div>
          <div className="min-w-0 flex-1">{children}</div>
          {controls && (
            <div className="flex flex-shrink-0 items-center gap-0.5">
              {controls}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
